import React, { cloneElement, isValidElement, useEffect, useMemo, useState } from "react";
import { List, useListContext, useNotify, useResourceContext } from "react-admin";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

export const removeEmptyValues = (values) =>
  Object.fromEntries(
    Object.entries(values || {}).filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
  );

const sortFilterValue = (value) => {
  if (Array.isArray(value)) return value.map(sortFilterValue);
  if (!value || typeof value !== "object") return value;

  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = sortFilterValue(value[key]);
      return result;
    }, {});
};

export const areReportFiltersEqual = (left, right) =>
  JSON.stringify(sortFilterValue(removeEmptyValues(left))) ===
  JSON.stringify(sortFilterValue(removeEmptyValues(right)));

const EMPTY_FILTER_DEFAULT_VALUES = {};

const ManualReportFilterForm = ({
  filters = [],
  filterDefaultValues = EMPTY_FILTER_DEFAULT_VALUES,
  onApply,
  onDirtyChange,
  onReset,
  requiredFilters = ["start_date", "end_date"],
  syncWithLocation = true,
}) => {
  const { displayedFilters, filterValues, setFilters } = useListContext();
  const resource = useResourceContext();
  const notify = useNotify();
  const location = useLocation();
  const navigate = useNavigate();
  const formDefaultValues = useMemo(
    () => ({ ...filterDefaultValues, ...filterValues }),
    [filterDefaultValues, filterValues]
  );
  const form = useForm({ defaultValues: formDefaultValues });
  const { control, handleSubmit, reset } = form;
  const draftValues = useWatch({ control });
  const hasPendingChanges = !areReportFiltersEqual(
    draftValues,
    filterValues
  );

  useEffect(() => {
    reset(formDefaultValues);
  }, [formDefaultValues, reset]);

  useEffect(() => {
    onDirtyChange?.(hasPendingChanges);
  }, [hasPendingChanges, onDirtyChange]);

  const handleReset = () => {
    const filterSources = new Set(
      filters.map((filterElement) => filterElement.props.source)
    );
    const emptyFilterValues = filters.reduce((values, filterElement) => {
      if (!requiredFilters.includes(filterElement.props.source)) {
        values[filterElement.props.source] = filterElement.props.reference
          ? null
          : "";
      }
      return values;
    }, {});
    const preservedDefaults = Object.fromEntries(
      Object.entries(filterDefaultValues).filter(
        ([source]) =>
          !filterSources.has(source) || requiredFilters.includes(source)
      )
    );
    const resetFilterValues = removeEmptyValues({
      ...emptyFilterValues,
      ...preservedDefaults,
    });

    reset(resetFilterValues);
    // Keep the applied filters in sync with the form reset. This also removes
    // optional filters from the URL, so a refresh cannot restore stale values.
    // Reset harus mengganti seluruh filter aktif, bukan mempertahankan
    // displayedFilters lama. ReferenceInput dapat meninggalkan nilai lama
    // (mis. goods_code) di URL walaupun input visual sudah kosong.
    setFilters(resetFilterValues, {}, false);
    if (syncWithLocation) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("filter", JSON.stringify(resetFilterValues));
      searchParams.set("displayedFilters", "{}");
      searchParams.delete("page");
      navigate(
        {
          pathname: location.pathname,
          search: `?${searchParams.toString()}`,
        },
        { replace: true }
      );
    }
    onReset?.();
  };

  const submit = handleSubmit((values) => {
    // Some custom inputs can be visually populated while their value is not
    // present in the submit payload after the list state is restored. Use the
    // current list/default value only for a field that is truly absent; an
    // explicitly cleared field must remain empty and fail validation.
    const valuesWithFallbacks = { ...values };
    requiredFilters.forEach((source) => {
      if (!Object.prototype.hasOwnProperty.call(valuesWithFallbacks, source)) {
        valuesWithFallbacks[source] =
          filterValues[source] ?? filterDefaultValues[source];
      }
    });
    const normalizedValues = removeEmptyValues(
      Object.fromEntries(
        Object.entries(valuesWithFallbacks).map(([source, value]) => [
          source,
          value && typeof value === "object" && "id" in value
            ? value.id
            : value,
        ])
      )
    );
    const missingFilter = requiredFilters.find(
      (source) => !normalizedValues[source]
    );

    if (missingFilter) {
      notify("Tanggal wajib diisi sebelum laporan ditampilkan", {
        type: "warning",
      });
      return;
    }

    if (
      normalizedValues.start_date &&
      normalizedValues.end_date &&
      normalizedValues.start_date > normalizedValues.end_date
    ) {
      notify("Tanggal mulai tidak boleh melewati tanggal berakhir", {
        type: "warning",
      });
      return;
    }

    setFilters(normalizedValues, displayedFilters, false);
    reset(normalizedValues);
    onApply?.(normalizedValues);
  });

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          display: "flex",
          flex: "1 1 auto",
          flexWrap: "wrap",
          alignItems: "center",
          minWidth: 0,
          minHeight: 62,
          py: 0.5,
          "& .manual-report-filter": {
            display: "flex",
            alignItems: "center",
            height: 54,
            pr: 2,
          },
          "& .manual-report-filter .MuiFormControl-root": {
            mt: 0,
            mb: 0,
          },
          "& .manual-report-filter-actions": {
            display: "inline-flex",
            alignItems: "center",
            flex: "0 0 auto",
            whiteSpace: "nowrap",
            gap: 1,
          },
          "& .MuiFormHelperText-root": { display: "none" },
        }}
      >
        {filters.map((filterElement) => (
          <Box
            className="manual-report-filter filter-field"
            data-source={filterElement.props.source}
            key={filterElement.key || filterElement.props.source}
          >
            {cloneElement(filterElement, {
              resource,
              record: {},
              size: filterElement.props.size || "small",
              helperText: false,
              defaultValue: undefined,
            })}
          </Box>
        ))}
        <Box className="manual-report-filter-actions">
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            sx={{ minWidth: 112, height: 40 }}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            sx={{ minWidth: 112, height: 40 }}
          >
            Tampilkan
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
};

const ReportPrompt = () => (
  <Paper
    elevation={0}
    sx={{
      minHeight: 112,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: 1,
      borderColor: "divider",
    }}
  >
    <Typography color="text.secondary">
      Periksa parameter laporan, lalu klik Tampilkan.
    </Typography>
  </Paper>
);

const ReportActionSlot = ({ action, disabled }) => {
  if (!isValidElement(action)) return action;
  return cloneElement(action, { disabled });
};

const ManualReportListState = ({
  actions,
  aside,
  children,
  filterDefaultValues,
  filters,
  pagination,
  queryOptions,
  requiredFilters,
  onApplied,
  onReset,
  // Resetting a report always returns it to the initial prompt. A specific
  // report can still opt out by explicitly passing resetClearsReport={false}.
  resetClearsReport = true,
  ...listProps
}) => {
  const [hasRun, setHasRun] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const manualFilters = useMemo(
    () => (
      <ManualReportFilterForm
        filters={filters}
        filterDefaultValues={filterDefaultValues}
        requiredFilters={requiredFilters}
        syncWithLocation={!listProps.disableSyncWithLocation}
        onApply={(appliedFilters) => {
          setHasRun(true);
          onApplied?.(appliedFilters);
        }}
        onDirtyChange={setIsDirty}
        onReset={() => {
          if (resetClearsReport) {
            setHasRun(false);
          }
          onReset?.();
        }}
      />
    ),
    [
      filterDefaultValues,
      filters,
      requiredFilters,
      onApplied,
      onReset,
      resetClearsReport,
      listProps.disableSyncWithLocation,
    ]
  );

  const reportActions = useMemo(() => {
    if (!isValidElement(actions)) return actions;
    return (
      <ReportActionSlot
        action={actions}
        disabled={!hasRun || isDirty}
      />
    );
  }, [actions, hasRun, isDirty]);

  return (
    <List
      {...listProps}
      actions={reportActions}
      aside={hasRun ? aside : null}
      filters={manualFilters}
      filterDefaultValues={filterDefaultValues}
      pagination={hasRun ? pagination : false}
      queryOptions={{
        ...queryOptions,
        enabled: hasRun && (queryOptions?.enabled ?? true),
      }}
    >
      {hasRun ? children : <ReportPrompt />}
    </List>
  );
};

const ManualReportList = ({
  filterDefaultValues = EMPTY_FILTER_DEFAULT_VALUES,
  ...props
}) => {
  const defaultFilterSignature = JSON.stringify(filterDefaultValues);

  return (
    <ManualReportListState
      key={defaultFilterSignature}
      filterDefaultValues={filterDefaultValues}
      {...props}
    />
  );
};

export default ManualReportList;
